import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress,
  Fade, Chip, LinearProgress, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider, Tab, Tabs
} from '@mui/material';
import {
  Mail, Send, Eye, Pause, Play, X, RefreshCw,
  CheckCircle2, AlertTriangle, Clock, Mailbox, Gauge
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const SectionCard = styled(Paper)(() => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: 24,
  marginBottom: 24,
}));

const DEFAULT_BODY = `<h2 style="color:#fff;font-size:22px;margin:0 0 16px;">Olá {{nome}}, novo método liberado para você!</h2>

<p style="color:#D1D5DB;font-size:15px;line-height:1.6;margin:0 0 16px;">
A Meta endureceu a análise de Business Manager em 2026. Os métodos antigos estão sendo recusados em massa.
</p>

<p style="color:#D1D5DB;font-size:15px;line-height:1.6;margin:0 0 16px;">
Por isso lançamos o <strong style="color:#A78BFA;">novo método de verificação de BM via FTM Legacy</strong>: você consulta o CNPJ, a IA gera o site institucional automaticamente, você aponta seu domínio próprio e ativa SSL — tudo em poucos minutos.
</p>

<div style="background:rgba(124,58,237,0.08);border:1px solid rgba(124,58,237,0.25);border-radius:12px;padding:20px;margin:24px 0;">
  <p style="color:#A78BFA;font-size:14px;font-weight:700;margin:0 0 10px;text-transform:uppercase;letter-spacing:0.5px;">
    O que mudou
  </p>
  <ul style="color:#D1D5DB;font-size:14px;line-height:1.8;margin:0;padding-left:20px;">
    <li>Use seu próprio domínio (.com.br, .com, .shop, etc)</li>
    <li>SSL gerado automaticamente após apontamento</li>
    <li>E-mail corporativo no mesmo domínio (contato@seudominio.com.br)</li>
    <li>Aprovação muito mais alta na análise da Meta</li>
  </ul>
</div>

<div style="text-align:center;margin:32px 0 16px;">
  <a href="https://ftmpainel.com.br/ftm-legacy" style="display:inline-block;background:linear-gradient(135deg,#7C3AED,#6D28D9);color:#fff;text-decoration:none;padding:14px 36px;border-radius:12px;font-size:16px;font-weight:700;">
    Acessar FTM Legacy agora
  </a>
</div>

<p style="color:#9CA3AF;font-size:13px;line-height:1.6;margin:24px 0 0;text-align:center;">
Tem dúvidas? Assista a vídeo aula completa direto no painel ao fazer login.
</p>`;

export default function AdminBroadcast() {
  const [tab, setTab] = useState(0);
  const [subject, setSubject] = useState('Novo método de verificação de BM em 2026 — disponível agora');
  const [preheader, setPreheader] = useState('Use seu domínio próprio + IA + SSL automático. Aprovação muito mais alta na Meta.');
  const [htmlBody, setHtmlBody] = useState(DEFAULT_BODY);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [detail, setDetail] = useState(null);
  const [config, setConfig] = useState({ intervalSeconds: 90, perDay: 960, provider: 'gmail' });
  const [savingConfig, setSavingConfig] = useState(false);

  const API = process.env.REACT_APP_API_URL;
  const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const fetchBroadcasts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/broadcast/list`, authHeader());
      if (res.data.success) setBroadcasts(res.data.broadcasts);
    } catch (err) {
      setError('Erro ao carregar campanhas');
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/broadcast/config`, authHeader());
      if (res.data.success) setConfig({ intervalSeconds: res.data.intervalSeconds, perDay: res.data.perDay, provider: res.data.provider });
    } catch (err) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API]);

  useEffect(() => { fetchBroadcasts(); fetchConfig(); }, [fetchBroadcasts, fetchConfig]);

  useEffect(() => {
    const i = setInterval(fetchBroadcasts, 15000);
    return () => clearInterval(i);
  }, [fetchBroadcasts]);

  const handleChangeSpeed = async (seconds) => {
    setSavingConfig(true);
    setError(''); setSuccess('');
    try {
      const res = await axios.post(`${API}/api/admin/broadcast/config`, { intervalSeconds: seconds }, authHeader());
      if (res.data.success) {
        setConfig({ intervalSeconds: res.data.intervalSeconds, perDay: res.data.perDay, provider: res.data.provider || config.provider });
        setSuccess(`Velocidade alterada: 1 email a cada ${res.data.intervalSeconds}s (~${res.data.perDay}/dia).`);
      } else setError(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao mudar velocidade');
    } finally { setSavingConfig(false); }
  };

  const SPEED_PRESETS_GMAIL = [
    { sec: 180, label: 'Conservador', perDay: 480, risk: 'Sem risco', color: '#10B981' },
    { sec: 90, label: 'Recomendado', perDay: 960, risk: 'Baixo', color: '#A78BFA' },
    { sec: 60, label: 'Rápido', perDay: 1440, risk: 'Médio (pode dar throttle)', color: '#F59E0B' },
    { sec: 30, label: 'Máximo', perDay: 2880, risk: 'ALTO (pode bloquear conta)', color: '#EF4444' },
  ];

  const SPEED_PRESETS_RESEND = [
    { sec: 30, label: 'Lento', perDay: 2880, risk: 'Sem risco', color: '#10B981' },
    { sec: 10, label: 'Padrão', perDay: 8640, risk: 'Sem risco', color: '#A78BFA' },
    { sec: 5, label: 'Recomendado', perDay: 17280, risk: 'Sem risco', color: '#7C3AED' },
    { sec: 1, label: 'Turbo', perDay: 86400, risk: 'Limite Resend: 10/seg', color: '#F59E0B' },
  ];

  const SPEED_PRESETS = config.provider === 'resend' ? SPEED_PRESETS_RESEND : SPEED_PRESETS_GMAIL;

  const handleSendTest = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/broadcast/create`,
        { subject, htmlBody, preheader, testOnly: true }, authHeader());
      if (res.data.success) setSuccess(res.data.message);
      else setError(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao enviar teste');
    } finally { setLoading(false); }
  };

  const handleSendAll = async () => {
    setError(''); setSuccess(''); setLoading(true);
    try {
      const res = await axios.post(`${API}/api/admin/broadcast/create`,
        { subject, htmlBody, preheader, testOnly: false }, authHeader());
      if (res.data.success) {
        setSuccess(`Campanha criada! ${res.data.broadcast.total} emails na fila — envio começará em 30s, com 1 email a cada 3 minutos.`);
        setConfirmOpen(false);
        fetchBroadcasts();
        setTab(1);
      } else setError(res.data.message);
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar campanha');
    } finally { setLoading(false); }
  };

  const handleAction = async (id, action) => {
    try {
      await axios.post(`${API}/api/admin/broadcast/${id}/${action}`, {}, authHeader());
      fetchBroadcasts();
      if (detail?._id === id) handleOpenDetail(id);
    } catch (err) {
      setError(err.response?.data?.message || `Erro ao ${action}`);
    }
  };

  const handleOpenDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/api/admin/broadcast/${id}`, authHeader());
      if (res.data.success) setDetail(res.data.broadcast);
    } catch (err) { setError('Erro ao buscar detalhes'); }
  };

  const statusColor = (s) => ({
    queued: '#9CA3AF', sending: '#7C3AED', paused: '#F59E0B',
    completed: '#10B981', cancelled: '#EF4444',
  }[s] || '#6B7280');

  const statusLabel = (s) => ({
    queued: 'Na fila', sending: 'Enviando', paused: 'Pausada',
    completed: 'Concluída', cancelled: 'Cancelada',
  }[s] || s);

  const fmt = (d) => d ? new Date(d).toLocaleString('pt-BR') : '-';

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
          <Mailbox size={32} color="#7C3AED" />
          <Typography variant="h4" sx={{ color: '#fff' }}>Email Broadcast</Typography>
          <Chip
            label={config.provider === 'resend' ? 'Resend ativo' : 'Gmail SMTP'}
            size="small"
            sx={{
              ml: 1,
              background: config.provider === 'resend' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
              color: config.provider === 'resend' ? '#10B981' : '#F59E0B',
              fontWeight: 700, fontSize: '0.72rem',
            }}
          />
        </Box>
        <Typography sx={{ color: '#9CA3AF', mb: 3 }}>
          Envie emails em massa para todos os clientes. Velocidade atual: <strong style={{ color: '#A78BFA' }}>1 email a cada {config.intervalSeconds}s (~{config.perDay.toLocaleString('pt-BR')}/dia)</strong> — ajuste na aba Campanhas.
        </Typography>

        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mb: 3, borderBottom: '1px solid rgba(255,255,255,0.06)',
          '& .MuiTab-root': { color: '#9CA3AF', textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: '#A78BFA !important' },
          '& .MuiTabs-indicator': { background: '#7C3AED' },
        }}>
          <Tab label="Nova campanha" />
          <Tab label={`Campanhas (${broadcasts.length})`} />
        </Tabs>

        {tab === 0 && (
          <SectionCard>
            <TextField fullWidth label="Assunto" value={subject} onChange={e => setSubject(e.target.value)}
              sx={{ mb: 2 }} placeholder="Ex: Novo método de verificação de BM..." />
            <TextField fullWidth label="Preheader (preview no inbox)" value={preheader} onChange={e => setPreheader(e.target.value)}
              sx={{ mb: 2 }} helperText="Texto que aparece após o assunto na maioria dos clients (~80 caracteres)" />
            <TextField fullWidth label="Corpo HTML" value={htmlBody} onChange={e => setHtmlBody(e.target.value)}
              multiline rows={14} sx={{ mb: 2, '& textarea': { fontFamily: 'monospace', fontSize: '0.78rem' } }}
              helperText="Use {{nome}} para personalizar com o primeiro nome do destinatário" />

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button startIcon={<Eye size={16} />} onClick={() => setPreviewOpen(true)}
                sx={{ textTransform: 'none', color: '#9CA3AF' }}>Preview</Button>
              <Button startIcon={<Mail size={16} />} onClick={handleSendTest} disabled={loading}
                variant="outlined" sx={{ textTransform: 'none', borderColor: 'rgba(124,58,237,0.4)', color: '#A78BFA' }}>
                Enviar teste pra mim
              </Button>
              <Button startIcon={<Send size={16} />} onClick={() => setConfirmOpen(true)}
                disabled={loading || !subject.trim() || !htmlBody.trim()} variant="contained"
                sx={{ textTransform: 'none', background: '#7C3AED', fontWeight: 700, '&:hover': { background: '#6D28D9' } }}>
                Enviar pra todos os clientes
              </Button>
            </Box>
          </SectionCard>
        )}

        {tab === 1 && (
          <>
            <SectionCard sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Gauge size={20} color="#A78BFA" />
                <Typography sx={{ color: '#fff', fontWeight: 700 }}>Velocidade de envio</Typography>
                <Chip label={`${config.intervalSeconds}s • ~${config.perDay}/dia`} size="small"
                  sx={{ ml: 'auto', background: 'rgba(124,58,237,0.15)', color: '#A78BFA', fontWeight: 600 }} />
              </Box>
              <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mb: 1.5 }}>
                Aplica em todas as campanhas em andamento. Mudança imediata, sem reiniciar.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
                {SPEED_PRESETS.map(p => {
                  const active = config.intervalSeconds === p.sec;
                  return (
                    <Button key={p.sec} onClick={() => handleChangeSpeed(p.sec)} disabled={savingConfig || active}
                      sx={{
                        textTransform: 'none', flexDirection: 'column', alignItems: 'flex-start', p: 1.5,
                        border: `1px solid ${active ? p.color : 'rgba(255,255,255,0.08)'}`, borderRadius: 2,
                        background: active ? `${p.color}15` : 'rgba(255,255,255,0.02)',
                        '&:hover': { background: `${p.color}10`, borderColor: p.color },
                        '&.Mui-disabled': active ? { background: `${p.color}15`, color: '#fff' } : {},
                      }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', mb: 0.5 }}>
                        <Typography sx={{ color: p.color, fontSize: '0.78rem', fontWeight: 700 }}>{p.label}</Typography>
                        {active && <CheckCircle2 size={14} color={p.color} />}
                      </Box>
                      <Typography sx={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>1/{p.sec}s</Typography>
                      <Typography sx={{ color: '#9CA3AF', fontSize: '0.7rem' }}>~{p.perDay}/dia</Typography>
                      <Typography sx={{ color: p.color, fontSize: '0.65rem', mt: 0.5 }}>{p.risk}</Typography>
                    </Button>
                  );
                })}
              </Box>
            </SectionCard>

          <SectionCard>
            {loadingList ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress sx={{ color: '#7C3AED' }} /></Box>
            : broadcasts.length === 0 ? <Typography sx={{ color: '#6B7280', textAlign: 'center', py: 4 }}>Nenhuma campanha ainda</Typography>
            : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {broadcasts.map(b => {
                  const pct = b.total > 0 ? Math.round((b.sentCount / b.total) * 100) : 0;
                  return (
                    <Box key={b._id} sx={{ p: 2.5, borderRadius: 2, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '0.95rem', mb: 0.3 }}>{b.subject}</Typography>
                          <Typography sx={{ color: '#6B7280', fontSize: '0.75rem' }}>Criada em {fmt(b.createdAt)}</Typography>
                        </Box>
                        <Chip label={statusLabel(b.status)} size="small"
                          sx={{ background: `${statusColor(b.status)}20`, color: statusColor(b.status), fontWeight: 600, fontSize: '0.72rem' }} />
                      </Box>

                      <Box sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem' }}>
                            <span style={{ color: '#10B981' }}>✓ {b.sentCount}</span>
                            {b.failedCount > 0 && <span style={{ color: '#EF4444', marginLeft: 12 }}>✗ {b.failedCount}</span>}
                            <span style={{ color: '#6B7280', marginLeft: 12 }}>de {b.total}</span>
                          </Typography>
                          <Typography sx={{ color: '#A78BFA', fontSize: '0.78rem', fontWeight: 600 }}>{pct}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={pct} sx={{
                          borderRadius: 2, height: 6, background: 'rgba(255,255,255,0.05)',
                          '& .MuiLinearProgress-bar': { background: statusColor(b.status), borderRadius: 2 }
                        }} />
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <Tooltip title="Detalhes"><IconButton size="small" onClick={() => handleOpenDetail(b._id)} sx={{ color: '#9CA3AF' }}><Eye size={16} /></IconButton></Tooltip>
                        {b.status === 'sending' && (
                          <Tooltip title="Pausar"><IconButton size="small" onClick={() => handleAction(b._id, 'pause')} sx={{ color: '#F59E0B' }}><Pause size={16} /></IconButton></Tooltip>
                        )}
                        {b.status === 'paused' && (
                          <Tooltip title="Retomar"><IconButton size="small" onClick={() => handleAction(b._id, 'resume')} sx={{ color: '#10B981' }}><Play size={16} /></IconButton></Tooltip>
                        )}
                        {b.failedCount > 0 && b.status !== 'cancelled' && (
                          <Tooltip title="Reenviar falhas"><IconButton size="small" onClick={() => handleAction(b._id, 'retry-failed')} sx={{ color: '#7C3AED' }}><RefreshCw size={16} /></IconButton></Tooltip>
                        )}
                        {['sending', 'paused', 'queued'].includes(b.status) && (
                          <Tooltip title="Cancelar"><IconButton size="small" onClick={() => handleAction(b._id, 'cancel')} sx={{ color: '#EF4444' }}><X size={16} /></IconButton></Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </SectionCard>
          </>
        )}

        {/* Preview */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { background: '#0F1219', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 3 } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ color: '#fff', fontWeight: 700 }}>Preview do email</Typography>
            <IconButton onClick={() => setPreviewOpen(false)} size="small" sx={{ color: '#6B7280' }}><X size={18} /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0, background: '#0F1219' }}>
            <Box sx={{ p: 2, background: '#0F1219' }}>
              <Typography sx={{ color: '#9CA3AF', fontSize: '0.78rem', mb: 0.5 }}>De: Painel FTM</Typography>
              <Typography sx={{ color: '#F9FAFB', fontWeight: 700, fontSize: '1rem' }}>{subject}</Typography>
              {preheader && <Typography sx={{ color: '#6B7280', fontSize: '0.82rem', mt: 0.5 }}>{preheader}</Typography>}
            </Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />
            <Box sx={{ background: '#141926', p: 3 }}
              dangerouslySetInnerHTML={{ __html: htmlBody.replace(/\{\{nome\}\}/g, 'Cliente') }} />
          </DialogContent>
        </Dialog>

        {/* Confirm send all */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ sx: { background: '#141926', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 3 } }}>
          <DialogTitle sx={{ color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlertTriangle size={20} color="#F59E0B" /> Confirmar envio em massa
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#D1D5DB', mb: 2 }}>
              Esta ação vai criar uma campanha e iniciar o envio de email para <strong style={{ color: '#A78BFA' }}>todos os clientes cadastrados</strong>.
            </Typography>
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.85rem' }}>
              Velocidade: <strong>1 email a cada 3 minutos</strong> (~480/dia, dentro do limite seguro do Gmail).
              Para 1.381 clientes, o envio levará ~3 dias. Você pode pausar ou cancelar a qualquer momento.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setConfirmOpen(false)} sx={{ textTransform: 'none', color: '#6B7280' }}>Cancelar</Button>
            <Button onClick={handleSendAll} disabled={loading} variant="contained"
              startIcon={loading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Send size={14} />}
              sx={{ textTransform: 'none', background: '#7C3AED', fontWeight: 700, '&:hover': { background: '#6D28D9' } }}>
              {loading ? 'Criando...' : 'Sim, enviar pra todos'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Detail */}
        <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth
          PaperProps={{ sx: { background: '#141926', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3 } }}>
          {detail && (
            <>
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>{detail.subject}</Typography>
                <IconButton onClick={() => setDetail(null)} size="small" sx={{ color: '#6B7280' }}><X size={18} /></IconButton>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
                  <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(16,185,129,0.06)', textAlign: 'center' }}>
                    <CheckCircle2 size={18} color="#10B981" style={{ marginBottom: 4 }} />
                    <Typography sx={{ color: '#10B981', fontWeight: 800, fontSize: '1.3rem' }}>{detail.sentCount}</Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.7rem' }}>Enviados</Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(239,68,68,0.06)', textAlign: 'center' }}>
                    <AlertTriangle size={18} color="#EF4444" style={{ marginBottom: 4 }} />
                    <Typography sx={{ color: '#EF4444', fontWeight: 800, fontSize: '1.3rem' }}>{detail.failedCount}</Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.7rem' }}>Falhas</Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, background: 'rgba(124,58,237,0.06)', textAlign: 'center' }}>
                    <Clock size={18} color="#A78BFA" style={{ marginBottom: 4 }} />
                    <Typography sx={{ color: '#A78BFA', fontWeight: 800, fontSize: '1.3rem' }}>{detail.total - detail.sentCount - detail.failedCount}</Typography>
                    <Typography sx={{ color: '#6B7280', fontSize: '0.7rem' }}>Pendentes</Typography>
                  </Box>
                </Box>

                <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mb: 1 }}>
                  Iniciada: {fmt(detail.startedAt)} • {detail.completedAt && `Concluída: ${fmt(detail.completedAt)}`}
                </Typography>

                {detail.failedRecipients?.length > 0 && (
                  <>
                    <Typography sx={{ color: '#EF4444', fontWeight: 600, fontSize: '0.85rem', mt: 2, mb: 1 }}>
                      Falhas ({detail.failedRecipients.length}{detail.failedCount > detail.failedRecipients.length ? `+, mostrando primeiras` : ''})
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto', background: 'rgba(239,68,68,0.04)', borderRadius: 1, p: 1 }}>
                      {detail.failedRecipients.map((r, i) => (
                        <Box key={i} sx={{ mb: 0.5 }}>
                          <Typography sx={{ color: '#FCA5A5', fontSize: '0.78rem', fontFamily: 'monospace' }}>{r.email}</Typography>
                          <Typography sx={{ color: '#6B7280', fontSize: '0.7rem', ml: 1 }}>{r.error}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </>
                )}
              </DialogContent>
            </>
          )}
        </Dialog>
      </Box>
    </Fade>
  );
}
